import { FormEvent, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CalendarCheck } from 'lucide-react';
import { api } from '@/lib/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui';

interface DemoRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_FORM = { firstName: '', lastName: '', email: '', message: '' };
const EMAIL_PATTERN = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

// Demo talep formu (anasayfa, fiyatlandırma). Giriş/şifre sıfırlama penceresiyle aynı görsel dil.
export const DemoRequestDialog = ({ open, onOpenChange }: DemoRequestDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof typeof EMPTY_FORM, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!EMAIL_PATTERN.test(form.email)) {
      toast.error(t('landingPage.notifications.invalidEmailToast'));
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/newsletter/demo-request', form);
      toast.success(t('landingPage.notifications.successToast'));
      onOpenChange(false);
      setForm(EMPTY_FORM);
    } catch {
      toast.error(t('landingPage.notifications.errorToast'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby='demo-request-description'
        className='max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] max-w-[540px] gap-0 overflow-y-auto rounded-[22px] border-[var(--public-line-faint)] bg-public-paper p-7 font-[Geist,ui-sans-serif,system-ui,sans-serif] shadow-[0_40px_80px_-40px_rgba(14,20,36,.55)] sm:rounded-[22px] sm:p-9'
      >
        <DialogHeader className='space-y-0 text-left sm:text-left'>
          <span className='flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-public-accent-soft'>
            <CalendarCheck className='h-6 w-6 text-public-accent' aria-hidden='true' />
          </span>
          <DialogTitle className='mt-5 text-[26px] font-semibold leading-[1.1] tracking-[-.03em] text-public-ink'>
            {t('landingPage.demoModal.title')}
          </DialogTitle>
          <DialogDescription id='demo-request-description' className='mt-2.5 text-[15px] leading-normal text-public-muted'>
            {t('landingPage.demoModal.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className='mt-7 grid gap-[18px]'>
          <div className='grid gap-[18px] sm:grid-cols-2 sm:gap-4'>
            <div>
              <Label htmlFor='demo-first-name' className='mb-2 block text-[13.5px] font-semibold text-public-ink'>
                {t('landingPage.demoModal.firstName')}
              </Label>
              <Input
                id='demo-first-name'
                autoComplete='given-name'
                value={form.firstName}
                onChange={(event) => handleChange('firstName', event.target.value)}
                placeholder={t('landingPage.demoModal.firstNamePlaceholder')}
                required
                className='h-[50px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 text-[15px]'
              />
            </div>
            <div>
              <Label htmlFor='demo-last-name' className='mb-2 block text-[13.5px] font-semibold text-public-ink'>
                {t('landingPage.demoModal.lastName')}
              </Label>
              <Input
                id='demo-last-name'
                autoComplete='family-name'
                value={form.lastName}
                onChange={(event) => handleChange('lastName', event.target.value)}
                placeholder={t('landingPage.demoModal.lastNamePlaceholder')}
                required
                className='h-[50px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 text-[15px]'
              />
            </div>
          </div>

          <div>
            <Label htmlFor='demo-email' className='mb-2 block text-[13.5px] font-semibold text-public-ink'>
              {t('landingPage.demoModal.email')}
            </Label>
            <Input
              id='demo-email'
              type='email'
              autoComplete='email'
              value={form.email}
              onChange={(event) => handleChange('email', event.target.value)}
              placeholder={t('landingPage.demoModal.emailPlaceholder')}
              required
              className='h-[50px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 text-[15px]'
            />
          </div>

          <div>
            <Label htmlFor='demo-message' className='mb-2 block text-[13.5px] font-semibold text-public-ink'>
              {t('landingPage.demoModal.message')}
            </Label>
            <Textarea
              id='demo-message'
              value={form.message}
              onChange={(event) => handleChange('message', event.target.value)}
              placeholder={t('landingPage.demoModal.messagePlaceholder')}
              className='min-h-[110px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 py-3 text-[15px]'
            />
          </div>

          <Button
            type='submit'
            disabled={isSubmitting}
            className='mt-2 h-[54px] w-full justify-between rounded-[12px] bg-public-ink px-[22px] text-[15.5px] font-semibold text-white shadow-[0_18px_30px_-18px_rgba(14,20,36,.6)] hover:bg-public-ink-hover active:bg-public-ink disabled:bg-public-ink disabled:opacity-60'
          >
            {isSubmitting ? t('landingPage.demoModal.submitting') : t('landingPage.demoModal.submit')}
            <ArrowRight className='h-[19px] w-[19px]' aria-hidden='true' />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
